import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Brain, TrendingUp, AlertTriangle, Clock, Eye, X, Minus, ChevronDown } from "lucide-react";
import { useState } from "react";

// Sem sugestões inventadas: as análises da IA serão geradas a partir dos
// dados reais das obras (integração com o assistente Obra IA). Até lá, o
// card exibe o estado vazio com atalho para o chat.
interface AISuggestion {
  id: number;
  type: string;
  title: string;
  description: string;
  priority: string;
  impact: string;
  action: string;
}
const suggestions: AISuggestion[] = [];

const getTypeIcon = (type: string) => {
  switch (type) {
    case "efficiency":
      return TrendingUp;
    case "cost":
      return Brain;
    case "risk":
      return AlertTriangle;
    case "maintenance":
      return Clock;
    default:
      return Brain;
  }
};

const getTypeColor = (type: string) => {
  switch (type) {
    case "efficiency":
      return "text-success bg-success/10";
    case "cost":
      return "text-primary bg-primary/10";
    case "risk":
      return "text-destructive bg-destructive/10";
    case "maintenance":
      return "text-warning bg-warning/10";
    default:
      return "text-muted-foreground bg-muted";
  }
};

const getPriorityVariant = (priority: string) => {
  switch (priority) {
    case "high":
      return "destructive";
    case "medium":
      return "default";
    case "low":
      return "outline";
    default:
      return "secondary";
  }
};

const getPriorityLabel = (priority: string) => {
  switch (priority) {
    case "high":
      return "Alta";
    case "medium":
      return "Média";
    case "low":
      return "Baixa";
    default:
      return "Média";
  }
};

interface AISuggestionsProps {
  isVisible?: boolean;
  onVisibilityChange?: (visible: boolean) => void;
}

export const AISuggestions = ({ isVisible = true, onVisibilityChange }: AISuggestionsProps) => {
  const [isMinimized, setIsMinimized] = useState(false);

  if (!isVisible) return null;

  return (
    <Card className="shadow-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-foreground">
          <div className="flex items-center gap-2">
            <Brain className="h-5 w-5 text-primary" />
            Sugestões Inteligentes da IA
          </div>
        </CardTitle>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="h-11 w-11 p-0 sm:h-6 sm:w-6"
            onClick={() => setIsMinimized(!isMinimized)}
          >
            {isMinimized ? <ChevronDown className="h-3 w-3" /> : <Minus className="h-3 w-3" />}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-11 w-11 p-0 sm:h-6 sm:w-6"
            onClick={() => onVisibilityChange?.(false)}
          >
            <X className="h-3 w-3" />
          </Button>
        </div>
      </CardHeader>
      {!isMinimized && (
        <CardContent className="space-y-4">
          {suggestions.length === 0 && (
            <div className="py-8 flex flex-col items-center justify-center text-center text-sm text-muted-foreground gap-2">
              <Brain className="h-8 w-8 opacity-40" />
              <span>
                As sugestões inteligentes serão geradas a partir dos dados das
                suas obras. Use o assistente <strong>Obra IA</strong> para
                análises sob demanda.
              </span>
            </div>
          )}
          {suggestions.map((suggestion) => {
            const IconComponent = getTypeIcon(suggestion.type);
            return (
              <div 
                key={suggestion.id}
                className="p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className={`p-2 rounded-full ${getTypeColor(suggestion.type)}`}>
                      <IconComponent className="h-4 w-4" />
                    </div>
                    <div>
                      <h4 className="font-medium text-foreground text-sm">
                        {suggestion.title}
                      </h4>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Badge 
                      variant={getPriorityVariant(suggestion.priority)}
                      className={`text-xs ${suggestion.priority === 'medium' ? 'bg-yellow-400 text-yellow-900 hover:bg-yellow-400' : ''}`}
                    >
                      {getPriorityLabel(suggestion.priority)}
                    </Badge>
                    <Button variant="ghost" size="sm" className="h-11 w-11 p-0 sm:h-6 sm:w-6">
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
                
                <p className="text-xs text-muted-foreground mb-3">
                  {suggestion.description}
                </p>
                
                <div className="text-xs font-medium text-success mb-3">
                  💡 {suggestion.impact}
                </div>
                
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="flex-1">
                    <Eye className="h-3 w-3 mr-1" />
                    Ver detalhes
                  </Button>
                  <Button 
                    size="sm" 
                    className="flex-1"
                    variant={suggestion.priority === "high" ? "default" : "secondary"}
                  >
                    {suggestion.action === "urgent" ? "Ação urgente" :
                     suggestion.action === "approve" ? "Aprovar" :
                     suggestion.action === "review" ? "Revisar" : "Agendar"}
                  </Button>
                </div>
              </div>
            );
          })}
          
          {suggestions.length > 0 && (
            <Button variant="outline" className="w-full">
              Ver todas as sugestões
            </Button>
          )}
        </CardContent>
      )}
    </Card>
  );
};